import React from 'react';
import InstitutionTypeahead from 'shared/institution-typeahead/components/institution-typeahead';
import {capitalize} from 'lodash';
import TextButton from 'src/components/TextButton';
import {EducationPhase} from 'src/types/enums';
import {UseBooleanState} from 'src/hooks/useBoolean';
import {FieldMetaProps} from 'formik';
import {CurrentInstitution} from 'src/types/education';
import {ObjectOf} from 'src/types/store';
import {TypeaheadViewFooter} from 'src/components/TypeaheadViewFooter';
import InfoTooltip from '../../../shared/components/info-tooltip';
import {useLocation} from 'react-router-dom';
import styles from '../../../src/components/institution-typeahead-view.module.scss';

export interface InstitutionTypeaheadViewProps {
  parentId: FieldMetaProps<Pick<CurrentInstitution, 'parentId'>>;
  expansion: UseBooleanState;
  location: [number, number] | null;
  institutionName: FieldMetaProps<Pick<CurrentInstitution, 'name'>>;
  firstName: string;
  isImpostor: boolean;
  postalCodeText: string;
}

export const getText = (
  isImpostor: boolean,
  firstName: string,
  postalCode: string,
  onClickSearch: () => void
): ObjectOf<React.ReactNode> => ({
  placeholder: `Enter ${isImpostor ? `${firstName}'s` : 'your'} high school, city, or ${
    postalCode || 'ZIP code'
  }`,
  needHelpHeader: (
    <span>
      &nbsp; Need Help Finding {`${capitalize(isImpostor ? `${firstName}'s` : 'your')}`} High
      School?
    </span>
  ),
  needHelp: [
    <li key={0}>Try searching {isImpostor ? 'their' : 'your'} school&apos;s street address.</li>,
    <li key={1}>Try keywords (e.g. Wyatt instead of Oscar Dean Wyatt).</li>,
    <li key={2}>Middle schools and colleges aren&apos;t supported.</li>,
    <li key={3}>
      Still having trouble?{' '}
      <TextButton onClick={onClickSearch}>
        Search all schools in {`${isImpostor ? `${firstName}'s` : 'your'}`} area.
      </TextButton>
    </li>,
  ],
});

export default function HighSchoolInstitutionTypeaheadView({
  postalCodeText,
  expansion,
  isImpostor,
  firstName,
  institutionName,
  location,
  parentId,
}: InstitutionTypeaheadViewProps) {
  const text = getText(isImpostor, firstName, postalCodeText, expansion.setTrue);
  const {pathname} = useLocation();
  const eligibilityPage = () => pathname.split('/').pop() === 'eligibility';

  return (
    <>
      <InstitutionTypeahead
        institutionType={EducationPhase.HIGH_SCHOOL}
        location={location || undefined}
        typeaheadProps={{
          placeholder: text.placeholder,
          focusOnMount: !institutionName.value,
          onSelectEmptyResult: expansion.setTrue,
          cy: 'institutionTypeahead',
        }}
        noTopPadding
        {...(eligibilityPage() && {
          renderLabel: () => <span className={styles.label}>High School</span>,
        })}
      />
      {!eligibilityPage() ? (
        <TypeaheadViewFooter
          parentId={parentId}
          NeedHelp={text.needHelpHeader}
          NeedHelpListItems={text.needHelp}
        />
      ) : (
        <div className="mt-8 shared-font body bold">
          <InfoTooltip>
            <ul>{text.needHelp}</ul>
          </InfoTooltip>
          Need help finding your high school?
        </div>
      )}
    </>
  );
}
